import { closestCenter, DndContext, type KeyboardCoordinateGetter, KeyboardSensor, PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import { IcoImages16 } from '@onefootprint/icons';
import type { ImageComparisonMetric } from '@onefootprint/request-types/smart-blocks';
import { type ReactNode, useId } from 'react';
import ComparisonSlot from './comparison-slot';
import ComparisonValueDisplay from './comparison-value';
import DocumentFields from './document-fields';
import ImageCarousel from './image-carousel';
import ImagePane, { type ImagePaneProps } from './image-pane';
import type { SourceTileProps } from './source-tile';
import type { ComparisonImage, ComparisonSelection, SlotIndex } from './types';
import useComparison from './use-comparison';
type ImageComparisonProps = {
  images: ComparisonImage[];
  initialSelection?: ComparisonSelection;
  results?: ImageComparisonMetric[];
  title?: string;
  renderImage?: (props: ImagePaneProps) => ReactNode;
  renderSource?: (props: SourceTileProps) => ReactNode;
};
const slotCoordinates: KeyboardCoordinateGetter = (event, {
  context
}) => {
  const slot = event.code === 'ArrowLeft' || event.code === 'ArrowUp' ? 0 : event.code === 'ArrowRight' || event.code === 'ArrowDown' ? 1 : undefined;
  if (slot === undefined) return undefined;
  event.preventDefault();
  const rect = context.droppableRects.get(`slot:${slot}`);
  if (!rect || !context.collisionRect) return undefined;
  return {
    x: rect.left + (rect.width - context.collisionRect.width) / 2,
    y: rect.top + (rect.height - context.collisionRect.height) / 2
  };
};
const ImageComparison = ({
  images,
  initialSelection,
  results = [],
  title = 'Image comparison',
  renderImage = props => <ImagePane key={props.image.id} {...props} />,
  renderSource
}: ImageComparisonProps) => {
  const {
    slots,
    selection,
    activeSlot,
    setActiveSlot,
    selectImage,
    clearSlot,
    views,
    inspectImage,
    pan,
    dismiss
  } = useComparison(images, initialSelection);
  const dndId = useId();
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: slotCoordinates
  }));
  return <section aria-label={title} className="@container/image-comparison overflow-hidden rounded-xl border border-tertiary bg-primary text-primary">
      <header className="flex min-h-14 items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <IcoImages16 />
          <h2 className="text-label-2">{title}</h2>
        </div>
      </header>
      {results.length > 0 && <dl aria-label="Overall results" className="flex flex-wrap gap-x-8 gap-y-3 px-4 pb-4">
          {results.map((result, index) =>
      // biome-ignore lint/suspicious/noArrayIndexKey: the contract permits repeated result labels.
      <div key={`${index}:${result.key}`} className="flex min-w-0 items-center gap-2 text-label-2">
              <dt className="text-caption-2 text-tertiary">{result.key}</dt>
              <dd className="min-w-0">
                <ComparisonValueDisplay value={result.value} />
              </dd>
            </div>)}
        </dl>}
      <DndContext id={dndId} sensors={sensors} collisionDetection={args => args.pointerCoordinates ? pointerWithin(args) : closestCenter(args)} onDragEnd={({
      active,
      over
    }) => {
      const id = active.data.current?.imageId;
      const slot = over?.data.current?.slot;
      if (typeof id === 'string' && (slot === 0 || slot === 1)) selectImage(id, slot);
    }}>
        {images.length > 0 && <ImageCarousel images={images} selection={selection} activeSlot={activeSlot} onSelect={id_0 => selectImage(id_0)} renderSource={renderSource} />}
        <div className="grid gap-3 px-4 pb-4 @[640px]/image-comparison:grid-cols-2">
          {([0, 1] as const).map((slot_0: SlotIndex) => {
          const image = slots[slot_0];
          return <ComparisonSlot key={slot_0} slot={slot_0} active={activeSlot === slot_0} onActivate={() => setActiveSlot(slot_0)}>
                {image && renderImage({
              image,
              slot: slot_0,
              onClear: () => clearSlot(slot_0),
              inspection: views[image.id],
              onInspect: point => inspectImage(image.id, point),
              onPan: point_0 => pan(image.id, point_0),
              onDismiss: dismiss,
              children: search => <DocumentFields image={image} search={search} />
            })}
              </ComparisonSlot>;
        })}
        </div>
      </DndContext>
      {Object.keys(views).length > 0 && <p role="status" className="sr-only">
          Magnified. Drag to inspect; click again or press Escape to return.
        </p>}
    </section>;
};
export default ImageComparison;